// frontend/modules/sms/components/FakerSmsLogsTable.tsx
import React, { useState } from 'react';
import { Table, Tag, Button, Tooltip, Typography, Space, Modal, Popconfirm } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { ReloadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import {FakerSmsLogDTO} from "@/shared/api/model";

const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
    DELIVERED: 'success',
    SENT: 'processing',
    FAILED: 'error',
    PENDING: 'default',
};

interface Props {
    data: FakerSmsLogDTO[];
    loading: boolean;
    pagination: {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, size: number) => void;
    };
    onRefresh?: () => void;
    onClearLogs?: () => void;
    clearingLogs?: boolean;
}

const FakerSmsLogsTable: React.FC<Props> = ({ 
    data, 
    loading, 
    pagination, 
    onRefresh, 
    onClearLogs,
    clearingLogs = false 
}) => {
    const { dark } = useTheme();
    const [messageModalVisible, setMessageModalVisible] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState<FakerSmsLogDTO | null>(null);

    const handleViewMessage = (record: FakerSmsLogDTO) => {
        setSelectedMessage(record);
        setMessageModalVisible(true);
    };

    const extractOTP = (content: string): string | null => {
        const otpPatterns = [
            /\b(\d{4,6})\b/,
            /OTP[:\s]*(\d{4,6})/i,
            /code[:\s]*(\d{4,6})/i,
            /verification[:\s]*(\d{4,6})/i,
        ];
        
        for (const pattern of otpPatterns) {
            const match = content.match(pattern);
            if (match) return match[1] || match[0];
        }
        return null;
    };

    const columns: ColumnsType<FakerSmsLogDTO> = [
        {
            title: 'Message ID',
            dataIndex: 'messageId',
            key: 'messageId',
            width: 160,
            render: (v: string) => <Text code copyable={{ text: v }}>{v?.slice(0, 14)}…</Text>,
        },
        {
            title: 'Recipient',
            dataIndex: 'recipient',
            key: 'recipient',
            width: 140,
        },
        {
            title: 'Sender',
            dataIndex: 'sender',
            key: 'sender',
            width: 120,
        },
        {
            title: 'Content',
            dataIndex: 'content',
            key: 'content',
            width: 300,
            render: (v: string, record: FakerSmsLogDTO) => (
                <div className="flex items-center gap-1">
                    <Tooltip title={v}>
                        <span className="flex-1 truncate">{v}</span>
                    </Tooltip>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewMessage(record)}
                    />
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
        },
        {
            title: 'Service',
            dataIndex: 'serviceName',
            key: 'serviceName',
            width: 140,
        },
        {
            title: 'Reference',
            dataIndex: 'reference',
            key: 'reference',
            width: 140,
            render: (v: string) => v || <Text type="secondary">—</Text>,
        },
        {
            title: 'Sent At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: (v: string) =>
                v ? new Date(v).toLocaleString() : <Text type="secondary">—</Text>,
        },
    ];

    const paginationConfig: TablePaginationConfig = {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} messages`,
        onChange: pagination.onChange,
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <Typography.Title level={5} className={dark ? 'text-white' : ''} style={{ margin: 0 }}>
                    Faker / Mock SMS Logs
                </Typography.Title>
                <Space>
                    {onRefresh && (
                        <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>
                            Refresh
                        </Button>
                    )}
                    {onClearLogs && (
                        <Popconfirm
                            title="Clear all SMS logs?"
                            description="This will permanently delete all faker SMS logs for your institution."
                            onConfirm={onClearLogs}
                            okText="Yes, Clear All"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Button 
                                danger 
                                icon={<DeleteOutlined />} 
                                loading={clearingLogs}
                            >
                                Clear All Logs
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>
            <Table
                columns={columns}
                dataSource={data}
                rowKey={(r) => String(r.id ?? r.messageId)}
                loading={loading}
                pagination={paginationConfig}
                scroll={{ x: 900 }}
                className={dark ? 'dark-table' : ''}
                size="small"
            />
            
            <Modal
                title="SMS Message Details"
                open={messageModalVisible}
                onCancel={() => setMessageModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setMessageModalVisible(false)}>
                        Close
                    </Button>,
                ]}
                width={600}
            >
                {selectedMessage && (
                    <div className="space-y-4">
                        <div>
                            <Text strong>Message ID:</Text>
                            <div className="mt-1">
                                <Text code copyable={{ text: selectedMessage.messageId }}>
                                    {selectedMessage.messageId}
                                </Text>
                            </div>
                        </div>

                        <div>
                            <Text strong>Recipient:</Text>
                            <div className="mt-1">
                                <Text copyable={{ text: selectedMessage.recipient }}>
                                    {selectedMessage.recipient}
                                </Text>
                            </div>
                        </div>

                        <div>
                            <Text strong>Sender:</Text>
                            <div className="mt-1">
                                <Text>{selectedMessage.sender}</Text>
                            </div>
                        </div>

                        <div>
                            <Text strong>Full Message:</Text>
                            <div className="mt-1 p-3 bg-gray-100 dark:bg-gray-800 rounded border">
                                <Text copyable={{ text: selectedMessage.content }}>
                                    {selectedMessage.content}
                                </Text>
                            </div>
                        </div>

                        {(() => {
                            const otp = extractOTP(selectedMessage.content || '');
                            if (otp) {
                                return (
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800">
                                        <Text strong className="text-blue-700 dark:text-blue-300">
                                            Detected OTP/Code:
                                        </Text>
                                        <div className="mt-2">
                                            <Text
                                                code
                                                copyable={{ text: otp }}
                                                className="text-2xl font-bold"
                                            >
                                                {otp}
                                            </Text>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Text strong>Status:</Text>
                                <div className="mt-1">
                                    <Tag color={STATUS_COLORS[selectedMessage.status || ''] ?? 'default'}>
                                        {selectedMessage.status}
                                    </Tag>
                                </div>
                            </div>

                            <div>
                                <Text strong>Service:</Text>
                                <div className="mt-1">
                                    <Text>{selectedMessage.serviceName || '—'}</Text>
                                </div>
                            </div>

                            <div>
                                <Text strong>Reference:</Text>
                                <div className="mt-1">
                                    <Text>{selectedMessage.reference || '—'}</Text>
                                </div>
                            </div>

                            <div>
                                <Text strong>Sent At:</Text>
                                <div className="mt-1">
                                    <Text>
                                        {selectedMessage.createdAt
                                            ? new Date(selectedMessage.createdAt).toLocaleString()
                                            : '—'}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FakerSmsLogsTable;
